import React, { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import { AdRemoteClient } from "./ad_remote_client";
import PageStoreProvider, {
  type PageStoreScope,
} from "./store/page/PageStoreProvider";

// 보조 페이지들은 라우트 단위 코드 스플리팅(lazy) — 초기(콕핏/로그인) 번들에서
// 분리해 첫 로드를 가볍게 한다. 각 페이지는 해당 경로 진입 시에만 청크를 받는다.
const AdminConsole = lazy(() => import("./app/admin/admin_console"));
const ExternalCameraDisplayPage = lazy(
  () => import("./app/display/external_camera_display_page"),
);
const MqttTestLabPage = lazy(() => import("./app/test_lab/mqtt_test_lab_page"));
const VehiclePage = lazy(() => import("./app/vehicle/vehicle_page"));
const CaptureGallery = lazy(() => import("./app/capture/capture_gallery"));
// 게이밍 휠 실험실 — 휠에 무엇이 가능한지 실측하고 결과를 서버로 수집한다.
const WheelLabPage = lazy(() => import("./app/wheel_lab/wheel_lab_page"));
// 확인된 능력을 실제로 조작하는 페이지 — 조향 추종 / 센터링 / HID 진동.
const WheelControlPage = lazy(() => import("./app/wheel_lab/wheel_control_page"));
const WebrtcSenderPage = lazy(
  () => import("./app/webrtc_sender/webrtc_sender_page"),
);

function RouteFallback() {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        font: "600 13px var(--aw-font, system-ui)",
        color: "#9aa1ac",
        background: "#142134",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          aria-hidden="true"
          style={{ animation: "aw-spin 0.9s linear infinite" }}
        >
          <style>{`
            @keyframes aw-spin { to { transform: rotate(360deg); } }
            @media (prefers-reduced-motion: reduce) { svg { animation: none !important; } }
          `}</style>
          <circle cx="14" cy="14" r="11" stroke="#2f5d8c" strokeWidth="3" />
          <path
            d="M14 3 A11 11 0 0 1 25 14"
            stroke="#3a8fd0"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
        <span>불러오는 중…</span>
      </div>
    </div>
  );
}

export function getRouterBasename(baseUrl: string) {
  if (!baseUrl || baseUrl === "/") {
    return undefined;
  }

  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  return normalizedBaseUrl === "" ? undefined : normalizedBaseUrl;
}

export function App({ baseUrl = "/" }: { baseUrl?: string }) {
  const basename = getRouterBasename(baseUrl);

  const renderPage = (scope: PageStoreScope, page: React.ReactNode) => (
    <PageStoreProvider scope={scope}>{page}</PageStoreProvider>
  );

  return (
    <BrowserRouter basename={basename}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route
          path="/test"
          element={renderPage("test-lab", <MqttTestLabPage />)}
        />
        <Route
          path="/vehicle"
          element={renderPage("vehicle", <VehiclePage />)}
        />
        <Route path="/_capture" element={<CaptureGallery />} />
        <Route path="/wheel-lab" element={<WheelLabPage />} />
        <Route path="/wheel-control" element={<WheelControlPage />} />
        <Route
          path="/admin"
          element={renderPage("admin", <AdminConsole />)}
        />
        <Route
          path="/webrtc-sender"
          element={renderPage("webrtc-sender", <WebrtcSenderPage />)}
        />
        <Route
          path="/display/left"
          element={renderPage(
            "display-left",
            <ExternalCameraDisplayPage role="left" />,
          )}
        />
        <Route
          path="/display/right"
          element={renderPage(
            "display-right",
            <ExternalCameraDisplayPage role="right" />,
          )}
        />
        <Route
          path="*"
          element={renderPage(
            "remote-client",
            <AdRemoteClient baseUrl={baseUrl} />,
          )}
        />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
